import React, { useState } from "react";
import moment, { Moment } from "moment";

import {
  DateTimePicker,
  DateTimePickerProps,
} from "@mui/x-date-pickers/DateTimePicker";
import { TextFieldProps } from "@mui/material";

import { CalendarIcon } from "../../design-system/icons";
import { dateTimeViewRenderers } from "./dateTimePickerViewRenderers";
import useDateTimePickerKeyboardCommit from "./hooks/useDateTimePickerKeyboardCommit";
import { useTimeFormat } from "globals/hooks";

export type MoovsDateTimePickerProps = {
  onAccept: (date: any) => void;
  renderInputProps?: TextFieldProps;
  clearable?: boolean;
  name?: string;
  inputFormat?: string;
  hideTabs?: boolean;
  [key: string]: any;
};

function MoovsDateTimePicker(props: MoovsDateTimePickerProps) {
  const {
    renderInputProps = {},
    clearable = false,
    name,
    onAccept,
    inputFormat,
    hideTabs,
    value,
    ...otherProps
  } = props;

  // hooks
  const { is24Hour } = useTimeFormat();

  // state
  const [openDateTimePicker, setOpenDateTimePicker] = useState(false);
  const [dateTime, setDateTime] = useState<Moment | null>(null);

  const committedValue = value ? moment(value) : null;

  // event handlers
  const commitAcceptedValue = (date: Moment | null) => {
    onAccept(date);
    setOpenDateTimePicker(false);
  };

  const {
    handleBlur,
    handleKeyDown,
    markActionBarInteraction,
    recordValidation,
    resetCommitState,
  } = useDateTimePickerKeyboardCommit({
    committedValue,
    draftValue: dateTime,
    onCommit: commitAcceptedValue,
    onBlur: renderInputProps.onBlur,
    onKeyDown: renderInputProps.onKeyDown,
  });

  const handleClosePicker = () => {
    resetCommitState();
    setDateTime(committedValue);
    setOpenDateTimePicker(false);
  };
  const handleOpenPicker = () => {
    if (openDateTimePicker) {
      return;
    }

    resetCommitState();
    setDateTime(committedValue);
    setOpenDateTimePicker(true);
  };

  const handleChange: NonNullable<
    DateTimePickerProps<Moment>["onChange"]
  > = (date, context) => {
    recordValidation(context);
    setDateTime(date ? moment(date) : null);
  };

  const handleAccept = (date: Moment | null) => {
    commitAcceptedValue(date ? moment(date) : null);
  };

  const {
    onBlur: _onBlur,
    onKeyDown: _onKeyDown,
    ...textFieldProps
  } = renderInputProps;

  const dateTimeNow = moment(new Date());
  const useAmPm = !is24Hour;

  return (
    <DateTimePicker
      {...otherProps}
      value={openDateTimePicker ? dateTime : committedValue}
      open={openDateTimePicker}
      ampm={useAmPm}
      format={inputFormat}
      onChange={handleChange}
      onAccept={handleAccept}
      onClose={handleClosePicker}
      minDateTime={dateTimeNow}
      // complete date-only selections with the current time instead of
      // midnight (the default, from the clock views' day granularity)
      referenceDate={dateTimeNow}
      slots={{ openPickerIcon: CalendarIcon }}
      viewRenderers={dateTimeViewRenderers}
      timeSteps={{ minutes: 1 }}
      slotProps={{
        actionBar: {
          actions: clearable ? ["clear", "cancel", "accept"] : ["cancel", "accept"],
          onMouseDownCapture: markActionBarInteraction,
        },
        tabs: { hidden: hideTabs },
        textField: {
          name,
          onClick: handleOpenPicker,
          variant: "outlined",
          fullWidth: true,
          inputProps: {
            style: { cursor: "pointer" },
          },
          ...textFieldProps,
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
        },
      }}
    />
  );
}
export default MoovsDateTimePicker;
